'use client';
import React from 'react';
import { Search, X } from 'lucide-react';

interface InventoryFiltersProps {
  search: string;
  onSearchChange: (v: string) => void;
  activeRoom: string;
  onRoomChange: (v: string) => void;
  activeMember: string;
  onMemberChange: (v: string) => void;
  activeStatus: string;
  onStatusChange: (v: string) => void;
  activeCategory: string;
  onCategoryChange: (v: string) => void;
  rooms: string[];
  members: string[];
  categories: string[];
}

const STATUS_OPTIONS = [
  { key: 'filter-all', value: 'all', label: 'All Statuses' },
  { key: 'filter-unpriced', value: 'unpriced', label: 'Unpriced' },
  { key: 'filter-priced', value: 'priced', label: 'Priced' },
  { key: 'filter-price-researched', value: 'price_researched', label: 'Price Researched' },
  { key: 'filter-verified', value: 'verified', label: 'Verified' },
];

export default function InventoryFilters({
  search,
  onSearchChange,
  activeRoom,
  onRoomChange,
  activeMember,
  onMemberChange,
  activeStatus,
  onStatusChange,
  activeCategory,
  onCategoryChange,
  rooms,
  members,
  categories,
}: InventoryFiltersProps) {
  const hasActiveFilters =
    activeRoom !== 'all' ||
    activeMember !== 'all' ||
    activeStatus !== 'all' ||
    activeCategory !== 'all' ||
    search !== '';

  return (
    <div className="space-y-3">
      {/* Search + status row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search items by name, description, or notes…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => onStatusChange(opt.value)}
              className={[
                'px-3 py-2 text-xs font-medium rounded-lg border transition-all duration-150 whitespace-nowrap',
                activeStatus === opt.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-secondary-foreground hover:bg-secondary',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Category filter chips */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-medium text-muted-foreground mr-1">Category:</span>
          <button
            onClick={() => onCategoryChange('all')}
            className={`px-3 py-1 text-xs rounded-full border transition-all duration-150 ${activeCategory === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-secondary-foreground hover:bg-secondary'}`}
          >
            All Categories
          </button>
          {categories.map((cat) => (
            <button
              key={`cat-chip-${cat}`}
              onClick={() => onCategoryChange(cat)}
              className={`px-3 py-1 text-xs rounded-full border transition-all duration-150 ${activeCategory === cat ? 'bg-amber-600 text-white border-amber-600' : 'border-amber-200 text-amber-800 bg-amber-50 hover:bg-amber-100 dark:border-amber-700/40 dark:text-amber-300 dark:bg-amber-900/20 dark:hover:bg-amber-900/40'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Room filter chips */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-medium text-muted-foreground mr-1">Room:</span>
        <button
          onClick={() => onRoomChange('all')}
          className={`px-3 py-1 text-xs rounded-full border transition-all duration-150 ${activeRoom === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-secondary-foreground hover:bg-secondary'}`}
        >
          All Rooms
        </button>
        {rooms.map((room) => (
          <button
            key={`room-chip-${room}`}
            onClick={() => onRoomChange(room)}
            className={`px-3 py-1 text-xs rounded-full border transition-all duration-150 ${activeRoom === room ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-secondary-foreground hover:bg-secondary'}`}
          >
            {room}
          </button>
        ))}
      </div>

      {/* Member filter chips */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-medium text-muted-foreground mr-1">Added by:</span>
        <button
          onClick={() => onMemberChange('all')}
          className={`px-3 py-1 text-xs rounded-full border transition-all duration-150 ${activeMember === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-secondary-foreground hover:bg-secondary'}`}
        >
          Everyone
        </button>
        {members.map((member) => (
          <button
            key={`member-chip-${member}`}
            onClick={() => onMemberChange(member)}
            className={`px-3 py-1 text-xs rounded-full border transition-all duration-150 ${activeMember === member ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-secondary-foreground hover:bg-secondary'}`}
          >
            {member}
          </button>
        ))}
      </div>

      {/* Active filter clear */}
      {hasActiveFilters && (
        <button
          onClick={() => {
            onSearchChange('');
            onRoomChange('all');
            onMemberChange('all');
            onStatusChange('all');
            onCategoryChange('all');
          }}
          className="flex items-center gap-1 text-xs text-negative hover:text-red-700 font-medium transition-colors"
        >
          <X size={12} /> Clear all filters
        </button>
      )}
    </div>
  );
}